import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, WritableSignal, inject, input, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { TranslatePipe } from '@ngx-translate/core';
import { EMPTY, catchError, of, switchMap, take, tap } from 'rxjs';

import { MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatInput } from '@angular/material/input';

import { MemberProfilesService } from '@lib/members/data-access';

import { ActionCommentRequestBody, ActionResponseBody, ActionStatus, ActionType, EntityType } from '@lib/core/models/action.interface';
import { PagedResponse } from '@lib/core/models/paged-response';

const MEMBER_COMMENT_MAX_LENGTH = 1000;

@Component({
  selector: 'members-comments-list',
  standalone: true,
  imports: [DatePipe, MatIcon, ReactiveFormsModule, MatInput, MatFormField, MatLabel, TranslatePipe],
  templateUrl: './member-comments-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MemberCommentsListComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly memberProfilesService = inject(MemberProfilesService);

  readonly memberId = input.required<string>();

  protected readonly comments: WritableSignal<ActionResponseBody[]> = signal([]);
  protected readonly loading: WritableSignal<boolean> = signal(false);
  protected readonly updatedComment: WritableSignal<ActionResponseBody | null> = signal(null);

  protected readonly commentForm = this.fb.group({
    id: this.fb.control<string | null>(null),
    text: this.fb.control<string | null>(null, [Validators.maxLength(MEMBER_COMMENT_MAX_LENGTH)]),
  });

  ngOnInit() {
    this.getMemberComments();
  }

  protected submitCommentAction() {
    this.loading.set(true);
    if (this.updatedComment()) {
      this.updateCommentAction(this.commentForm.value.id, this.commentForm.value.text);
    } else {
      this.addCommentAction();
    }
  }

  protected enableCommentUpdateProcess(commentAction: ActionResponseBody) {
    this.updatedComment.set(commentAction);
    this.commentForm.patchValue({ id: commentAction.id, text: commentAction.content });
  }

  protected cancelCommentUpdateProcess() {
    this.updatedComment.set(null);
    this.commentForm.reset();
  }

  protected deleteCommentAction(actionId: string) {
    this.memberProfilesService
      .deleteClientProfileComment(actionId)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.comments.update(comments => comments.filter(comment => comment.id !== actionId));
        },
        error: error => {
          console.group('Трапилася помилка при видаленні коментаря.');
          console.error(error);
          console.groupEnd();
        },
      });
  }

  private getMemberComments(): void {
    this.memberProfilesService
      .getClientProfileComments(this.memberId())
      .pipe(
        catchError(error => {
          console.group('Трапилася помилка при завантаженні коментарів користувача.');
          console.error(error);
          console.groupEnd();
          this.loading.set(false);
          return EMPTY;
        }),
        switchMap((commentsMeta: PagedResponse<ActionResponseBody>) => {
          const filteredActiveComments: ActionResponseBody[] = commentsMeta.value.filter(comment => comment.status === ActionStatus.Active);
          return of(filteredActiveComments);
        }),
        tap((comments: ActionResponseBody[]) => this.comments.set(comments)),
        take(1)
      )
      .subscribe();
  }

  private addCommentAction() {
    const commentRequestBody: ActionCommentRequestBody = {
      type: ActionType.Comment,
      entityType: EntityType.Member,
      entityId: this.memberId(),
      content: this.commentForm.value.text,
    };

    this.memberProfilesService
      .createClientProfileComment(commentRequestBody)
      .pipe(take(1))
      .subscribe({
        next: (newComment: ActionResponseBody) => {
          this.commentForm.reset();
          this.comments.update(comments => [newComment, ...comments]);
          this.loading.set(false);
        },
        error: error => {
          console.group('Трапилася невідома помилка при створенні комментаря. Спробуйте ще раз.');
          console.error(error);
          console.groupEnd();
          this.loading.set(false);
        },
      });
  }

  private updateCommentAction(actionId: string, actionContent: string) {
    this.memberProfilesService
      .updateClientProfileComment(actionId, actionContent)
      .pipe(take(1))
      .subscribe({
        next: (updatedComment: ActionResponseBody) => {
          this.comments.update(comments => comments.map(comment => (comment.id === actionId ? updatedComment : comment)));
          this.commentForm.reset();
          this.updatedComment.set(null);
          this.loading.set(false);
        },
        error: error => {
          console.group('Трапилася помилка при оновленні коментаря. Спробуйте ще раз.');
          console.error(error);
          console.groupEnd();
          this.loading.set(false);
        },
      });
  }
}
